import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import "./SearchableCombobox.css";

interface SearchableComboboxProps {
  items: { value: number | string; label: string }[];
  value: number | string | null;
  placeholder?: string;
  onChange: (value: number | string) => void;
}
export const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
  items,
  value,
  placeholder,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selectedLabel = items.find((i) => i.value === value)?.label || "";

  const filtered =
    query.length === 0
      ? items
      : items.filter((i) => i.label.toLowerCase().includes(query.toLowerCase()));

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPanelStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 99999,
    });
  }, []);

  const handleSelect = (v: string | number) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const handleFocus = () => {
    setOpen(true);
    setQuery("");
    updatePosition();
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (ref.current && !ref.current.contains(document.activeElement)) {
        setOpen(false);
        setQuery("");
      }
    }, 150);
  };

  useEffect(() => {
    const listener = (e: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        panelRef.current &&
        !panelRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScroll = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, updatePosition]);

  return (
    <div className="combo" ref={ref}>
      <div className={`combo-trigger ${open ? "combo-trigger--open" : ""}`}>
        <input
          ref={inputRef}
          className="combo-input"
          placeholder={placeholder}
          value={open ? query : selectedLabel}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <ChevronDown
          size={18}
          className={`combo-chevron ${open ? "combo-chevron--open" : ""}`}
          onClick={() => {
            if (open) {
              setOpen(false);
              setQuery("");
            } else {
              setOpen(true);
              inputRef.current?.focus();
            }
          }}
        />
      </div>

      {open &&
        createPortal(
          <div className="combo-panel" ref={panelRef} style={panelStyle}>
            <div className="combo-list">
              {filtered.length === 0 && <div className="combo-empty">Aucun résultat</div>}
              {filtered.map((item) => (
                <div
                  key={item.value}
                  className={`combo-item ${item.value === value ? "combo-item--selected" : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item.value)}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
export default SearchableCombobox;
